import React, { useEffect, useState } from "react";
import { createQuestionAssessment } from "../../services/question";
import { getCategory } from "../../services/catgeoriesServices";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { toast } from "react-toastify";
import LoadingButton from "@/components/Buttons/LoadingButton";
import "./style.css";

export default function CreateQuestionForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    index: 0,
    en_questionText: "",
    ar_questionText: "",
    type: "",
    value: 0,
    isMandatory: false,
    validation: { min: "", max: "" },
    options: [{ en_value: "", ar_value: "", value: 0 }],
    questionCategoryId: "",
  });

  const [categories, setCategories] = useState<{ id: string; en_name: string; ar_name: string }[]>([]);

  const [errors, setErrors] = useState<{
    en_questionText?: string;
    ar_questionText?: string;
    type?: string;
    questionCategoryId?: string;
    validation?: { min?: string; max?: string };
    options?: string;
  }>({});

  const fetchCategories = async () => {
    try {
      const result = await getCategory();
      setCategories(result);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!form.en_questionText.trim()) {
      newErrors.en_questionText =
        String(t("question.English Question is required")) || "English Question is required";
    }

    if (!form.ar_questionText.trim()) {
      newErrors.ar_questionText =
        String(t("question.Arabic Question is required")) || "Arabic Question is required";
    }

    if (!form.type) {
      newErrors.type = String(t("question.Type is required")) || "Type is required";
    }

    if (!form.questionCategoryId) {
      newErrors.questionCategoryId =
        String(t("question.Category is required")) || "Category is required";
    }

    if (form.type === "number" || form.type === "date") {
      const validationErrors: { min?: string; max?: string } = {};

      if (!form.validation.min || form.validation.min.trim() === "") {
        validationErrors.min =
          String(t("question.Validation Min is required")) || "Minimum value is required";
      }

      if (!form.validation.max || form.validation.max.trim() === "") {
        validationErrors.max =
          String(t("question.Validation Max is required")) || "Maximum value is required";
      }

      if (form.type === "number") {
        const minNum = parseFloat(form.validation.min);
        const maxNum = parseFloat(form.validation.max);

        if (!isNaN(minNum) && !isNaN(maxNum) && minNum >= maxNum) {
          validationErrors.max =
            String(t("question.Maximum must be greater than minimum")) ||
            "Maximum must be greater than minimum";
        }

        if (form.validation.min && isNaN(minNum)) {
          validationErrors.min =
            String(t("question.Minimum must be a valid number")) || "Minimum must be a valid number";
        }

        if (form.validation.max && isNaN(maxNum)) {
          validationErrors.max =
            String(t("question.Maximum must be a valid number")) || "Maximum must be a valid number";
        }
      }

      if (form.type === "date") {
        const minDate = new Date(form.validation.min);
        const maxDate = new Date(form.validation.max);

        if (form.validation.min && isNaN(minDate.getTime())) {
          validationErrors.min =
            String(t("question.Minimum must be a valid date")) || "Minimum must be a valid date";
        }

        if (form.validation.max && isNaN(maxDate.getTime())) {
          validationErrors.max =
            String(t("question.Maximum must be a valid date")) || "Maximum must be a valid date";
        }

        if (!isNaN(minDate.getTime()) && !isNaN(maxDate.getTime()) && minDate >= maxDate) {
          validationErrors.max =
            String(t("question.Maximum date must be greater than minimum date")) ||
            "Maximum date must be greater than minimum date";
        }
      }

      if (Object.keys(validationErrors).length > 0) {
        newErrors.validation = validationErrors;
      }
    }

    if (["single_choice", "multiple_choice", "yes_no"].includes(form.type)) {
      if (form.type === "yes_no") {
        if (!form.options || form.options.length !== 2) {
          newErrors.options =
            String(t("question.Yes/No must have exactly two options")) ||
            "Yes/No must have exactly two options";
        }
      } else {
        const validOptions = form.options.filter((opt) => (opt.en_value || "").trim());
        if (validOptions.length === 0) {
          newErrors.options =
            String(t("question.At least one option is required")) || "At least one option is required";
        } else if (validOptions.length < 2) {
          newErrors.options =
            String(t("question.At least two options are required")) || "At least two options are required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === "checkbox";
    setForm((prev) => {
      const updated: Record<string, unknown> = {
        ...prev,
        [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value,
      };

      if (name === "type") {
        if (value === "yes_no") {
          updated.options = [
            { en_value: "Yes", ar_value: "نعم", value: 0 },
            { en_value: "No", ar_value: "لا", value: 0 },
          ];
        } else if (value === "single_choice" || value === "multiple_choice") {
          updated.options = [{ en_value: "", ar_value: "", value: 0 }];
        } else {
          updated.options = [{ en_value: "", ar_value: "", value: 0 }];
        }
      }

      return updated as typeof prev;
    });

    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof typeof errors];
        return newErrors;
      });
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...form.options];
    updatedOptions[index].en_value = value;
    setForm({ ...form, options: updatedOptions });

    if (errors.options) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.options;
        return newErrors;
      });
    }
  };

  const addOption = () => {
    setForm({ ...form, options: [...form.options, { en_value: "", ar_value: "", value: 0 }] });
  };

  const handleValidationChange = (field: "min" | "max", value: string) => {
    setForm({
      ...form,
      validation: {
        ...form.validation,
        [field]: value,
      },
    });

    if (errors.validation?.[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors.validation) {
          delete newErrors.validation[field];
          if (Object.keys(newErrors.validation).length === 0) {
            delete newErrors.validation;
          }
        }
        return newErrors;
      });
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      toast.error(
        String(t("question.Please fix the validation errors")) || "Please fix the validation errors"
      );
      return;
    }

    const computedOptions = ["single_choice", "multiple_choice", "yes_no"].includes(form.type)
      ? form.options
          .filter((opt) => (form.type === "yes_no" ? true : (opt.en_value || "").trim()))
          .map((opt) => ({
            en_value: opt.en_value,
            ar_value: opt.ar_value,
            value: Number(opt.value) || 0,
          }))
      : undefined;

    const summedValue = computedOptions
      ? computedOptions.reduce((sum, opt) => sum + (Number(opt.value) || 0), 0)
      : Number(form.value) || 0;

    const payload = {
      ...form,
      value: summedValue,
      index: Number(form.index),
      validation:
        form.type === "number" || form.type === "date"
          ? {
              min: form.validation.min,
              max: form.validation.max,
            }
          : undefined,
      options: computedOptions,
    } as Record<string, unknown>;

    try {
      await createQuestionAssessment(payload);
      toast.success(String(t("question.Create Question successful")));
      navigate("/questionassessment");
    } catch (error: unknown) {
      console.error("create error:", error);
      const msg = error instanceof Error ? error.message : String(t("An error occurred during create"));
      toast.error(msg || String(t("An error occurred during create")));
    }
  };

  const errClass = (has: boolean) => (has ? "is-invalid" : "");

  return (
    <div className="root qa-create-page">
      <Breadcrumb className="qa-create-breadcrumb">
        <BreadcrumbItem>{t("question.Question")}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem className="text-orange" active>
          {t("question.Create Question")}
        </BreadcrumbItem>
      </Breadcrumb>

      <header className="qa-create-header">
        <h1 className="qa-create-title">{t("question.Create Question")}</h1>
        <p className="qa-create-subtitle qa-create-subtitle--compact">
          {t("question.create_question_subtitle")}
        </p>
      </header>

      <div className="qa-create-form-card">
        <div className="qa-create-section">
          <h2 className="qa-create-section-title">
            {t("question.create_section_questions")}
          </h2>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="qa-create-label" htmlFor="qa-en-q">
                {t("question.English Question")}
              </label>
              <textarea
                id="qa-en-q"
                name="en_questionText"
                rows={2}
                className={`form-control qa-create-input qa-create-textarea ${errClass(!!errors.en_questionText)}`}
                onChange={handleChange}
                value={form.en_questionText}
                autoComplete="off"
              />
              {errors.en_questionText ? (
                <div className="qa-create-error">{errors.en_questionText}</div>
              ) : null}
            </div>
            <div className="col-md-6">
              <label className="qa-create-label" htmlFor="qa-ar-q">
                {t("question.Arabic Question")}
              </label>
              <textarea
                id="qa-ar-q"
                name="ar_questionText"
                rows={2}
                className={`form-control qa-create-input qa-create-textarea ${errClass(!!errors.ar_questionText)}`}
                onChange={handleChange}
                value={form.ar_questionText}
                dir="rtl"
                autoComplete="off"
              />
              {errors.ar_questionText ? (
                <div className="qa-create-error">{errors.ar_questionText}</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="qa-create-section">
          <h2 className="qa-create-section-title">
            {t("question.create_section_settings")}
          </h2>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="qa-create-label" htmlFor="qa-type">
                {t("question.Type")}
              </label>
              <select
                id="qa-type"
                name="type"
                className={`form-control qa-create-select ${errClass(!!errors.type)}`}
                onChange={handleChange}
                value={form.type}
              >
                <option value="">{t("question.select_type")}</option>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="yes_no">Yes / No</option>
                <option value="date">Date</option>
                <option value="single_choice">Single Choice</option>
                <option value="multiple_choice">Multiple Choice</option>
              </select>
              {errors.type ? <div className="qa-create-error">{errors.type}</div> : null}
            </div>

            {["text", "number", "date"].includes(form.type) ? (
              <div className="col-md-6">
                <label className="qa-create-label" htmlFor="qa-value">
                  {String(t("question.Value") || "Value")}
                </label>
                <input
                  id="qa-value"
                  name="value"
                  type="number"
                  className="form-control qa-create-input"
                  onChange={handleChange}
                  value={form.value}
                />
              </div>
            ) : null}

            <div className="col-md-6">
              <label className="qa-create-label" htmlFor="qa-cat">
                {t("question.Category")}
              </label>
              <select
                id="qa-cat"
                name="questionCategoryId"
                className={`form-control qa-create-select ${errClass(!!errors.questionCategoryId)}`}
                onChange={handleChange}
                value={form.questionCategoryId}
              >
                <option value="">{t("question.select_category")}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.en_name} — {cat.ar_name}
                  </option>
                ))}
              </select>
              {errors.questionCategoryId ? (
                <div className="qa-create-error">{errors.questionCategoryId}</div>
              ) : null}
            </div>

            <div className="col-md-6 d-flex align-items-end">
              <label className="qa-create-check">
                <input
                  type="checkbox"
                  name="isMandatory"
                  checked={form.isMandatory}
                  onChange={handleChange}
                />
                <span>{t("question.is_mandatory_label")}</span>
              </label>
            </div>
          </div>
        </div>

        {form.type === "number" || form.type === "date" ? (
          <div className="qa-create-section">
            <h2 className="qa-create-section-title">
              {t("question.create_section_validation")}
            </h2>
            <div className="row g-2">
              <div className="col-md-6">
                <label className="qa-create-label" htmlFor="qa-min">
                  {t("question.Validation Min")}
                </label>
                <input
                  id="qa-min"
                  name="min"
                  type={form.type === "date" ? "date" : "number"}
                  className={`form-control qa-create-input ${errClass(!!errors.validation?.min)}`}
                  value={form.validation.min}
                  onChange={(e) => handleValidationChange("min", e.target.value)}
                />
                {errors.validation?.min ? (
                  <div className="qa-create-error">{errors.validation.min}</div>
                ) : null}
              </div>
              <div className="col-md-6">
                <label className="qa-create-label" htmlFor="qa-max">
                  {t("question.Validation Max")}
                </label>
                <input
                  id="qa-max"
                  name="max"
                  type={form.type === "date" ? "date" : "number"}
                  className={`form-control qa-create-input ${errClass(!!errors.validation?.max)}`}
                  value={form.validation.max}
                  onChange={(e) => handleValidationChange("max", e.target.value)}
                />
                {errors.validation?.max ? (
                  <div className="qa-create-error">{errors.validation.max}</div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {["single_choice", "multiple_choice", "yes_no"].includes(form.type) ? (
          <div className="qa-create-section qa-create-section--flush">
            <h2 className="qa-create-section-title">{t("question.Options")}</h2>
            <div className="qa-create-options">
              {form.options.map((opt, idx) => (
                <div key={idx} className="qa-create-option-row">
                  <div className="qa-create-option-field">
                    <span className="qa-create-option-badge">EN</span>
                    <input
                      type="text"
                      className="form-control qa-create-input"
                      placeholder={`EN ${idx + 1}`}
                      value={opt.en_value}
                      disabled={form.type === "yes_no"}
                      onChange={(e) => handleOptionChange(idx, e.target.value)}
                    />
                  </div>
                  <div className="qa-create-option-field">
                    <span className="qa-create-option-badge">AR</span>
                    <input
                      type="text"
                      className="form-control qa-create-input"
                      placeholder={`AR ${idx + 1}`}
                      value={opt.ar_value}
                      disabled={form.type === "yes_no"}
                      onChange={(e) => {
                        const updated = [...form.options];
                        updated[idx].ar_value = e.target.value;
                        setForm({ ...form, options: updated });
                      }}
                      dir="rtl"
                    />
                  </div>
                  <div className="qa-create-option-field qa-create-option-field--narrow">
                    <span className="qa-create-option-badge">#</span>
                    <input
                      type="number"
                      className="form-control qa-create-input"
                      placeholder={String(t("question.Value") || "Value")}
                      value={opt.value}
                      onChange={(e) => {
                        const updated = [...form.options];
                        updated[idx].value = Number(e.target.value) || 0;
                        setForm({ ...form, options: updated });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {errors.options ? <div className="qa-create-error qa-create-error--block">{errors.options}</div> : null}
            {form.type !== "yes_no" ? (
              <button type="button" className="qa-create-btn-outline" onClick={addOption}>
                + {t("question.Add Option")}
              </button>
            ) : null}
          </div>
            ) : null}

        <div className="qa-create-actions">
          <button type="button" className="qa-create-btn-muted" onClick={() => navigate("/questionassessment")}>
            {t("question.cancel_back")}
          </button>
          <LoadingButton
            onClick={handleSubmit}
            className="qa-create-submit btn"
            variant="success"
            loadingText={String(t("question.Submitting...") || "Submitting...")}
          >
            {String(t("question.Submit Question"))}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
